/* eslint-disable camelcase */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Button, CircularProgress } from '@nextiva/next-ui-components';

import { bulkUpdateLicenseAssignment } from 'api/licenseService';
import { useRootUserList } from 'customHooks/userCustomHooks';
import {
  LicensesContext,
  actionTypes,
} from 'containers/pages/BillingLicenses/licensesContext';
import { LICENSES } from 'constants/routes';
import { useSnackbar } from 'hooks';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import styles from '../../LicensesTabPanel/snackbar.scss';
import { SaveChangesModal } from '../SaveChangesModal/SaveChangesModal.tsx';
import EditAssignmentWarningModal from '../EditAssignmentWarningModal';

const UNASSIGNMENT_WARNING_THRESHOLD = 10;

export const DrawerFooter = ({ history, toggleDrawer }) => {
  const {
    rootState: { rootUserList },
    updateRootUserList,
  } = useRootUserList();
  const queryClient = useQueryClient();

  const {
    licensesState: {
      selectedUserList,
      bulkRequestBody,
      editLicenseAssignment: {
        name,
        license_count,
        licenseCount,
        id: licenseId,
        number_of_licenses_assigned,
        numberOfLicensesAssigned,
        number_of_licenses_available,
        numberOfLicensesAvailable,
        license_definition,
        licenseDefinition,
      },
      saveChangesModalOpen,
      pristine,
      editLicenseAssignment,
    },
    dispatch,
  } = useContext(LicensesContext);
  const [sending, setSending] = useState(false);
  const [removeCount, setRemoveCount] = useState(0);

  const setGlobalSnackbar = useSnackbar();
  const setDrawerSnackbar = useSnackbar(true);
  const {
    featureState: { authV3Enabled },
    corpAcctNbr,
  } = useContext(FeatureContext);

  const assigned = number_of_licenses_assigned || numberOfLicensesAssigned;
  const available = number_of_licenses_available || numberOfLicensesAvailable;

  useEffect(() => {
    setRemoveCount(
      bulkRequestBody.filter((item) => item.action === 'REMOVE').length,
    );
    if (
      bulkRequestBody.length === 0 ||
      selectedUserList.length > (license_count || licenseCount)
    ) {
      dispatch({
        type: actionTypes.SET_PRISTINE,
        pristine: true,
      });

      return;
    }
    dispatch({
      type: actionTypes.SET_PRISTINE,
      pristine: false,
    });
  }, [bulkRequestBody]);

  const onCancelHandler = () => {
    dispatch({
      type: actionTypes.SET_PRISTINE,
      pristine: true,
    });
    toggleDrawer();

    dispatch({
      type: actionTypes.RESET_EDIT_ASSIGNMENT_DRAWER,
    });
    // clear license param
    history.push(LICENSES);
  };

  const onSaveHandler = (event, togglePurchaseLicenseModal = false) => {
    dispatch({
      type: actionTypes.SET_PRISTINE,
      pristine: true,
    });
    setSending(true);
    bulkUpdateLicenseAssignment(
      licenseId,
      bulkRequestBody,
      authV3Enabled,
      corpAcctNbr,
    )
      .then((response) => {
        let fails = [];
        if (authV3Enabled) {
          fails = response?.errors?.filter(
            (update) => update.result === 'FAIL',
          );
        } else {
          fails = response.filter((update) => update.result === 'FAIL');
        }
        let removeFails = 0;
        let addFails = 0;

        updateRootUserList();

        if (fails?.length > 0) {
          fails.forEach((fail) => {
            if (fail.action === 'REMOVE') {
              removeFails += 1;
            }
            if (fail.action === 'ADD') {
              addFails += 1;
            }
          });

          const failedUsers = fails.map((failed) => {
            let found;
            if (authV3Enabled) {
              found = rootUserList.find(
                (user) => user.nextivaUuid === failed.nextivaUuid,
              );
            } else {
              found = rootUserList.find(
                (user) => user.profiles[0].id === Number(failed.profile_id),
              );
            }

            return `${found.first_name || found.firstName} ${
              found.last_name || found.lastName
            }`;
          });

          setDrawerSnackbar({
            message: (
              <span>
                <div className={styles.container__textBlock}>
                  The following users were not able to have their license
                  updated:
                </div>
                <div className={styles.container__textBlock}>
                  {failedUsers.map((failedName) => (
                    <div>{failedName}</div>
                  ))}
                </div>
                <div>
                  Please try again, if you continue to receive this message
                  please contact support at 1-800-285-7995.
                </div>
              </span>
            ),
            severity: 'error',
          });
          setSending(false);

          return;
        }

        setTimeout(() => {
          setSending(false);
          toggleDrawer();
          if (togglePurchaseLicenseModal) {
            dispatch({
              type: actionTypes.TOGGLE_PURCHASE_LICENSE_MODAL,
              license: editLicenseAssignment,
            });
          }
          setTimeout(() => {
            setGlobalSnackbar({
              message: (
                <span>
                  The{' '}
                  <b>
                    {name.includes('NUC-')
                      ? license_definition?.description ||
                        licenseDefinition.description
                      : name}
                  </b>{' '}
                  license was successfully updated.
                </span>
              ),
              severity: 'success',
            });
          }, [1000]);

          history.push(LICENSES);
        }, 3000);

        dispatch({
          type: actionTypes.UPDATE_VIEW_LIST,
          update: {
            id: licenseId,
            assigned: assigned + removeFails - addFails,
            available: available - removeFails + addFails,
            authV3Enabled,
          },
        });
        queryClient.invalidateQueries(['licenseList']);
      })
      .catch(() => {
        setDrawerSnackbar({
          message: 'The license could not be updated.',
          severity: 'error',
        });
        setSending(false);
      });
  };

  const confirmSaveChanges = (event) => {
    if (removeCount > UNASSIGNMENT_WARNING_THRESHOLD) {
      dispatch({
        type: actionTypes.TOGGLE_TEN_REMOVALS_MODAL,
      });
    } else {
      onSaveHandler(event);
    }
  };

  return (
    <>
      {saveChangesModalOpen && (
        <SaveChangesModal onSaveHandler={confirmSaveChanges} />
      )}
      {
        <EditAssignmentWarningModal
          onConfirm={onSaveHandler}
          numOfLicenseRemovals={removeCount}
        />
      }
      <div className="m-drawer-footer">
        <div className="m-drawer-footer__container">
          <div className="m-drawer-footer__container__action-bar">
            <div className="m-drawer-footer__container__action-bar__button">
              <Button
                variant="contained"
                color="secondary"
                onClick={onCancelHandler}
                data-testid="PlatformAdminUI-Licensing-DrawerFooter-CancelButton"
              >
                Cancel
              </Button>
            </div>
            <div className="m-drawer-footer__container__action-bar__button">
              <Button
                variant="contained"
                color="primary"
                onClick={confirmSaveChanges}
                disabled={pristine}
                data-testid="PlatformAdminUI-Licensing-DrawerFooter-SaveButton"
              >
                Save
              </Button>
              {sending && (
                <div className="m-drawer-footer__container__action-bar__spinner">
                  <CircularProgress
                    style={{ color: '#009FE0' }}
                    mode="indeterminate"
                    size={36}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

DrawerFooter.propTypes = {
  toggleDrawer: PropTypes.func.isRequired,
};

export default withRouter(DrawerFooter);
